import React, {useEffect, useState, useRef} from "react";
import useAuth from "../../hooks/useAuth";
import jwt_decode from "jwt-decode";
import {updateEstimateData} from "../../api/pro/estimateReq";
import FormButtons from "../../components/common/FormButtons";
import {checkRequiredFields, getErrorListFromValidation, patternListChoices, removeErrorList, sortSections, validateFormInput} from "../../helper/tools";

export default function FormEditEstimate({apiEstimateList, estimateContentInfos, legend, sections, setShowModal, headerInfos, estimateList}) {
    const { auth } = useAuth();
    const token = auth.token;
    const decodedToken = jwt_decode(token);
    const agencyId = decodedToken.agency;
    const [ values, setValues ] = useState();
    const [ headerName, setHeaderName ] = useState(headerInfos?.headerName);
    const [ headerType, setHeaderType ] = useState(headerInfos?.headerType);
    const [ updatedValues, setUpdatedValues ] = useState([]);
    const [ headerAmount, setHeaderAmount ] = useState(0);
    const [ usualTotal, setUsualTotal ] = useState(0);
    const [ complementaryTotal, setComplementaryTotal ] = useState(0);
    const [ enabledSections, setEnabledSections ] = useState([]);
    const [ tvaSummary, setTvaSummary ] = useState({ tva0: 0, tva3: 0, tva20: 0 });
    const filteredArray = estimateList.filter(estimateCat => estimateCat?.categoryId === headerInfos?.categoryId)
    const headerTypeRef = useRef();

    const priceFieldNames = ["usualPrice", "complementaryPrice", "familyFeePrice"];

    const handleChange = (e, lineId) => {
        // La virgule décimale n'est qu'une convention d'affichage/saisie : le back attend un
        // float (point décimal), donc on la reconvertit avant de stocker la valeur envoyée à l'API.
        const value = priceFieldNames.includes(e.target.name)
            ? e.target.value.replace(",", ".")
            : e.target.value;
        setUpdatedValues(prevState =>
           prevState?.map((obj) => {
               if (obj.id === lineId) {
                   return {...obj, [e.target.name]: value}
               }
               return obj
           })
        )
    }

    const onChange = (e) => {
        if (priceFieldNames.includes(e.target.name)) {
            // Les prix des devis s'écrivent avec une virgule décimale (ex: 12,50) ; remplacer
            // automatiquement un point tapé par habitude/pavé numérique.
            e.target.value = e.target.value.replace(".", ",");
        }
        validateFormInput(e)
    }

    useEffect(() => {
        if (sections) {
            const filtered = sections.filter(section => section.enabled == 1);
            setEnabledSections(sortSections(filtered));
        }
    }, [ sections ]);

    const handleRemoveModal = (e) => {
        setShowModal(false)
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!checkRequiredFields()) {
            return;
        }

        let body = {
            agencyId: agencyId,
            headerId: headerInfos.headerId,
            categoryId : headerInfos.categoryId,
            type : headerType || 'C',
            description: headerName,
            amount : headerAmount,
            contentList: updatedValues
        }
        await updateEstimateData(token, body)
            .then(response => {
                removeErrorList();
                if(!response?.response?.data?.error) {
                    setShowModal(false)
                    apiEstimateList()
                } else {
                    getErrorListFromValidation(response?.response?.data?.error)
                }
            })
    }

    useEffect(() => {
        setValues(estimateContentInfos)
    }, [ estimateContentInfos ]);

    useEffect(() => {
        setUpdatedValues(values)
    }, [ values ]);

    useEffect(() => {
        if (!updatedValues) return;
        const valuesArray = Object.values(updatedValues);
        let sum = valuesArray.reduce((acc, value) => {
            const quantity = (value.dataValue && !isNaN(value?.dataValue)) ? parseFloat(value.dataValue) : 1;

            if (value.usualPrice && !isNaN(value?.usualPrice)) {
                acc += parseFloat(value.usualPrice) * quantity;
            }
            if (value.complementaryPrice && !isNaN(value?.complementaryPrice)) {
                acc += parseFloat(value.complementaryPrice) * quantity;
            }
            if (value.familyFeePrice && !isNaN(value?.familyFeePrice)) {
                acc += parseFloat(value.familyFeePrice) * quantity;
            }
            return acc;
        }, 0);
        setHeaderAmount(sum)

        let usualSum = 0;
        let complementarySum = 0;

        let tva0Total = 0;
        let tva3Total = 0;
        let tva20Total = 0;

        valuesArray.forEach(value => {
            const quantity = (value.dataValue && !isNaN(value?.dataValue)) ? parseFloat(value.dataValue) : 1;
            let lineTotal = 0;

            if (value.usualPrice && !isNaN(value?.usualPrice)) {
                const usualAmount = parseFloat(value.usualPrice) * quantity;
                lineTotal += usualAmount;
                usualSum += usualAmount;
            }
            if (value.complementaryPrice && !isNaN(value?.complementaryPrice)) {
                const complementaryAmount = parseFloat(value.complementaryPrice) * quantity;
                lineTotal += complementaryAmount;
                complementarySum += complementaryAmount;
            }
            if (value.familyFeePrice && !isNaN(value?.familyFeePrice)) {
                lineTotal += parseFloat(value.familyFeePrice) * quantity;
            }

            if (value.tvaRateId === 1) {
                tva0Total += lineTotal;
            } else if (value.tvaRateId === 2) {
                tva3Total += lineTotal;
            } else if (value.tvaRateId === 3) {
                tva20Total += lineTotal;
            }
        });

        setUsualTotal(usualSum);
        setComplementaryTotal(complementarySum);
        setTvaSummary({ tva0: tva0Total, tva3: tva3Total, tva20: tva20Total });
    }, [ updatedValues ]);

    useEffect(() => {
        setHeaderName(headerInfos?.headerName);
        setHeaderType(headerInfos?.headerType);
    }, [ headerInfos ]);

    useEffect(() => {
        if (headerInfos?.headerId === 0 && !headerType) {
            const hasCremation = filteredArray.some(estimate => estimate.headerType === "C");
            const hasInhumation = filteredArray.some(estimate => estimate.headerType === "I");

            if (hasCremation && !hasInhumation) {
                setHeaderType("I");
            }
            else if (!hasCremation && hasInhumation) {
                setHeaderType("C");
            }
        }
    }, [ headerInfos, filteredArray, headerType ]);

    const handleChangeHeader = (e) => {
        setHeaderName(e.target.value)
        validateFormInput(e)
    }
    const handleChangeHeaderType = (e) => {
        setHeaderType(e.target.value)
        validateFormInput(e)
    };

    return (
        <form onSubmit={handleSubmit} method="POST" noValidate>
            <fieldset className="no-modal-form">
                <legend>{legend}</legend>
                <div className="dashboard devis-type">
                    <div className="form-input">
                        <label className="required">Description (obligatoire)</label>
                        <input pattern={patternListChoices?.name} type="text" value={headerName || ''} onChange={handleChangeHeader} required/>
                    </div>
                    { headerInfos?.headerId == 0 &&
                        <div className="form-input">
                            <label className="required">Type de devis</label>
                            <select ref={headerTypeRef} value={headerType || ''} name="type" onChange={handleChangeHeaderType} required>
                                {(() => {
                                    const hasCremation = filteredArray.some(estimate => estimate.headerType === "C");
                                    const hasInhumation = filteredArray.some(estimate => estimate.headerType === "I");

                                    if (!hasCremation && !hasInhumation) {
                                        return (
                                            <>
                                                <option value="">-- Choisir un type --</option>
                                                <option value="C">Crémation</option>
                                                <option value="I">Inhumation</option>
                                            </>
                                        );
                                    }
                                    else if (hasCremation && !hasInhumation) {
                                        return <option value="I">Inhumation</option>;
                                    }
                                    else if (!hasCremation && hasInhumation) {
                                        return <option value="C">Crémation</option>;
                                    }
                                    else {
                                        return <option value="">Les deux types existent déjà</option>;
                                    }
                                })()}
                            </select>
                        </div>
                    }
                    <div className="form-input">
                        <label>Montant TTC</label>
                        <span>{headerAmount} €</span>
                    </div>
                    { headerInfos?.headerId != 0 &&
                        <div className="form-input">
                            <label>Type de devis</label>
                            <span>{headerType === "I" ? "Inhumation" : headerType === "C" ? "Crémation" : ""}</span>
                        </div>
                    }
                    {enabledSections?.map((section, index) => (
                        <table key={index}>
                            <thead>
                                <tr>
                                    <th>{section?.section} - {section?.description}</th>
                                    <th>Qté</th>
                                    <th>Prestations obligatoires TTC</th>
                                    <th>Prestations non obligatoires TTC</th>
                                </tr>
                            </thead>
                            <tbody>
                            {values?.filter(content => (content?.sectionId === section.id))?.map((line, index2) => (
                                <tr key={index2}>
                                    <td>{line?.description}</td>
                                    {line?.dataInput ?
                                        <td>
                                            <div className="form-input">
                                                <input pattern={patternListChoices?.fileName} type="text" name="dataValue"
                                                       onChange={onChange}
                                                       defaultValue={headerInfos?.headerId != 0 ? line?.dataValue : 0}
                                                       onBlur={(e) => handleChange(e, line?.id)}/>
                                            </div>
                                        </td>
                                        : <td></td>
                                    }
                                    {line?.usualAvailable ?
                                        <td>
                                            <div className="form-input">
                                                <input pattern={patternListChoices?.price} type="text" name="usualPrice"
                                                       onChange={onChange}
                                                       defaultValue={headerInfos?.headerId != 0 ? line?.usualPrice : 0}
                                                       onBlur={(e) => handleChange(e, line?.id)}/>
                                            </div>
                                        </td>
                                        : <td></td>
                                    }
                                    {line?.complementaryAvailable ?
                                        <td>
                                            <div className="form-input">
                                                <input pattern={patternListChoices?.price} type="text" name="complementaryPrice"
                                                       onChange={onChange}
                                                       defaultValue={headerInfos?.headerId != 0 ? line?.complementaryPrice : 0}
                                                       onBlur={(e) => handleChange(e, line?.id)}/>
                                            </div>
                                        </td>
                                        : <td></td>
                                    }
                                    {/*{line?.familyFeeAvailable ?
                                        <td>
                                            <div className="form-input">
                                                <input pattern={patternListChoices?.price} type="text" name="familyFeePrice"
                                                       onChange={onChange}
                                                       defaultValue={headerInfos?.headerId != 0 ? line?.familyFeePrice : 0}
                                                       onBlur={(e) => handleChange(e, line?.id)}/>
                                            </div>
                                        </td>
                                        : <td></td>
                                    }*/}
                                    {/*{line?.tvaRateId &&
                                        <td>
                                            <div className="form-input">
                                                <select name="tvaRateId" defaultValue={headerInfos?.headerId != 0 && line?.tvaRateId}
                                                        onChange={(e) => handleChange(e, line?.id)}>
                                                    <option name="tvaRateId" value="1">0.00 %</option>
                                                    <option name="tvaRateId" value="2">10.00 %</option>
                                                    <option name="tvaRateId" value="3">20.00 %</option>
                                                </select>
                                            </div>
                                        </td>
                                    }*/}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    ))}

                    <div className="subtotal-row">
                        <div className="subtotal-label">Sous-totaux</div>
                        <div className="subtotal-qte"></div>
                        <div className="subtotal-amounts">
                            <div className="subtotal-item">
                                <span className="subtotal-value">{usualTotal.toFixed(2)} €</span>
                            </div>
                            <div className="subtotal-item">
                                <span className="subtotal-value">{complementaryTotal.toFixed(2)} €</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tva-summary-container flex">
                    <table className="tva-summary-table">
                        <thead>
                            <tr>
                                <th>Base HT</th>
                                <th>TVA</th>
                                <th>Montant TVA</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tvaSummary.tva0 > 0 && (
                                <tr>
                                    <td>{(tvaSummary.tva0).toFixed(2)} €</td>
                                    <td>0%</td>
                                    <td>{(0).toFixed(2)} €</td>
                                </tr>
                            )}
                            {tvaSummary.tva3 > 0 && (
                                <tr>
                                    <td>{(tvaSummary.tva3 / 1.03).toFixed(2)} €</td>
                                    <td>3%</td>
                                    <td>{(tvaSummary.tva3 - (tvaSummary.tva3 / 1.03)).toFixed(2)} €</td>
                                </tr>
                            )}
                            {tvaSummary.tva20 > 0 && (
                                <tr>
                                    <td>{(tvaSummary.tva20 / 1.20).toFixed(2)} €</td>
                                    <td>20%</td>
                                    <td>{(tvaSummary.tva20 - (tvaSummary.tva20 / 1.20)).toFixed(2)} €</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <table className="tva-totals-table">
                        <tbody>
                            <tr>
                                <td><strong>Total HT</strong></td>
                                <td><strong>{((tvaSummary.tva0) + (tvaSummary.tva3 / 1.03) + (tvaSummary.tva20 / 1.20)).toFixed(2)} €</strong></td>
                            </tr>
                            <tr>
                                <td><strong>Total TVA</strong></td>
                                <td><strong>{((tvaSummary.tva3 - (tvaSummary.tva3 / 1.03)) + (tvaSummary.tva20 - (tvaSummary.tva20 / 1.20))).toFixed(2)} €</strong></td>
                            </tr>
                            <tr>
                                <td><strong>Total TTC</strong></td>
                                <td><strong>{(tvaSummary.tva0 + tvaSummary.tva3 + tvaSummary.tva20).toFixed(2)} €</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div>
                    <p>
                        (1) Les opérations réalisées sont soumises au taux normal de TVA de 20 %, sauf les opérations de transport de corps, avant et après mise en bière, et la fourniture de plants horticoles d'ornement, de plantes vivantes, de fleurs fraîches et de fleurs séchées, non transformés, auxquelles s'applique le taux intermédiaire de 10 %.<br/>
                        (2) La dispersion des cendres peut avoir lieu en pleine nature et peut être effectuée soit à titre gratuit par la famille elle-même, soit par un opérateur funéraire mandaté et rémunéré à cet effet.<br/>
                        (3) Frais non soumis à TVA.
                    </p>
                </div>
                <div className="errors"></div>
            </fieldset>
            <FormButtons handleRemoveModal={handleRemoveModal}/>
        </form>
    )
}